"use client";

import { isEmpty, last } from "lodash";
import { ArrowLeft } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { usePathname, useRouter } from "@/i18n/navigation";
import { useCallback, useEffect } from "react";

const STORAGE_KEY = "gate_history";

type Props = {
  hidden?: boolean;
};

type GateHistory = {
  pathname: string;
  searchParams: String;
};

export const GoBack = ({ hidden = false }: Props) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const getStorageValue = async (): Promise<GateHistory[]> => {
    try {
      const value = sessionStorage.getItem(STORAGE_KEY);

      if (!value) {
        return [];
      }

      return JSON.parse(value) ?? [];
    } catch {
      return [];
    }
  };

  const clearOnGoBack = async (path: string) => {
    const value = await getStorageValue();
    const lastHistoryItem = last(value);

    if (!lastHistoryItem || lastHistoryItem.pathname !== path) {
      return;
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value.slice(0, -1)));
  };

  const updateStorage = useCallback(
    async (path: string) => {
      const value = await getStorageValue();
      const lastHistoryItem = last(value);

      if (!lastHistoryItem) {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify([
            { pathname: path, searchParams: searchParams.toString() },
          ])
        );
        return;
      }

      if (lastHistoryItem.pathname === path) {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(
            value.map((v, index, arr) =>
              index === arr.length - 1
                ? { ...v, searchParams: searchParams.toString() }
                : v
            )
          )
        );
      }

      if (lastHistoryItem.pathname !== path) {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify([
            ...value,
            { pathname: path, searchParams: searchParams.toString() },
          ])
        );
      }
    },
    [searchParams]
  );

  useEffect(() => {
    updateStorage(pathname);
  }, [pathname, updateStorage, searchParams]);

  const backToResultsLink = (): string => {
    const priceRangeValue = searchParams.get("priceRange");
    const selectedHotelStarRatingsValue = searchParams.get(
      "selectedHotelStarRatings"
    );
    const amenitiesFilterValue = searchParams.get("amenitiesFilter");
    const selectedRatingValue = searchParams.get("selectedRating");
    const sortingValue = searchParams.get("sorting");
    const searchValueParam = searchParams.get("searchValue");
    const updatedPage = searchParams.get("page");

    const queryString = `priceRange=${encodeURIComponent(priceRangeValue || "")}&selectedHotelStarRatings=${encodeURIComponent(selectedHotelStarRatingsValue || "")}&amenitiesFilter=${encodeURIComponent(amenitiesFilterValue || "")}&selectedRating=${encodeURIComponent(selectedRatingValue || "")}&sorting=${encodeURIComponent(sortingValue || "")}&searchValue=${encodeURIComponent(searchValueParam || "")}&page=${encodeURIComponent(updatedPage || "")}`;

    const tokenId = searchParams.get("tokenId");
    const searchId = searchParams.get("searchId");
    const lastHotel = searchParams.get("giataId");

    return tokenId
      ? `/results?tokenId=${tokenId}&${queryString}&lastHotel=${lastHotel}`
      : `/results?searchId=${searchId}&${queryString}&lastHotel=${lastHotel}`;
  };

  const getGoBackPath = async () => {
    const value = await getStorageValue();
    const previousItem = last(value.slice(0, -1));

    if (!previousItem) {
      return "/";
    }

    if (pathname === "/booking-processing") {
      return "/";
    }

    if (
      pathname === "/results/details" &&
      previousItem.pathname === "/results"
    ) {
      return backToResultsLink();
    }

    if (isEmpty(previousItem.searchParams)) {
      return previousItem.pathname;
    }

    return `${previousItem.pathname}?${previousItem.searchParams}`;
  };

  const goBack = async () => {
    const path = await getGoBackPath();
    await clearOnGoBack(pathname);
    router.push(path);
  };

  if (hidden) {
    return null;
  }

  return (
    <button className="text-white block p-2 bg-none" onClick={goBack}>
      <ArrowLeft className="block" />
    </button>
  );
};
